import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Strikethrough,
  List, ListOrdered,
  Undo2, Redo2,
  Minus
} from 'lucide-react';

const ToolbarBtn = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onMouseDown={e => { e.preventDefault(); onClick(); }}
    disabled={disabled}
    title={title}
    className={`h-7 w-7 flex items-center justify-center rounded-md text-sm transition-all
      ${active
        ? 'bg-orange-100 text-orange-600 shadow-sm'
        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'}
      ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    {children}
  </button>
);

const Divider = () => <div className="h-5 w-px bg-gray-200 mx-0.5" />;

export function RichTextEditor({ value, onChange, placeholder, minHeight = 160 }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: true,
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Notlarınızı buraya yazın...',
        emptyEditorClass: 'is-editor-empty',
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'tiptap-content outline-none',
      },
    },
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });

  if (!editor) return null;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-orange-300 focus-within:border-orange-300 transition-all bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-3 py-2 border-b border-gray-100 bg-gray-50/80">
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Kalın (Ctrl+B)"
        >
          <Bold className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="İtalik (Ctrl+I)"
        >
          <Italic className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
          title="Üstü Çizili"
        >
          <Strikethrough className="h-3.5 w-3.5" />
        </ToolbarBtn>

        <Divider />

        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Madde İmli Liste"
        >
          <List className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Numaralı Liste"
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarBtn>

        <Divider />

        <ToolbarBtn
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Yatay Çizgi"
        >
          <Minus className="h-3.5 w-3.5" />
        </ToolbarBtn>

        <Divider />

        <ToolbarBtn
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Geri Al (Ctrl+Z)"
        >
          <Undo2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Yinele (Ctrl+Y)"
        >
          <Redo2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
      </div>

      {/* Editor area */}
      <EditorContent
        editor={editor}
        style={{ minHeight }}
        className="px-4 py-3 text-sm text-gray-800 cursor-text"
        onClick={() => editor.commands.focus()}
      />

      <style>{`
        .tiptap-content p { margin: 0 0 0.5rem 0; line-height: 1.6; }
        .tiptap-content p:last-child { margin-bottom: 0; }
        .tiptap-content ul { list-style: disc; padding-left: 1.2rem; margin: 0.25rem 0; }
        .tiptap-content ol { list-style: decimal; padding-left: 1.2rem; margin: 0.25rem 0; }
        .tiptap-content li { margin: 0.15rem 0; }
        .tiptap-content strong { font-weight: 700; color: #1f2937; }
        .tiptap-content em { font-style: italic; }
        .tiptap-content s { text-decoration: line-through; color: #9ca3af; }
        .tiptap-content hr { border: none; border-top: 1px solid #e5e7eb; margin: 0.75rem 0; }
        .tiptap-content.is-editor-empty:first-child::before,
        .tiptap-content p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
          float: left;
          height: 0;
        }
      `}</style>
    </div>
  );
}
